// src/pages/dashboard/DashboardDekan.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  CheckSquare,
  AlertTriangle,
  Clock,
  TrendingUp,
  Building2,
  ChevronRight,
  AlertCircle,
  Hourglass,
  Eye,
  HelpCircle,
  Info,
  XCircle,
  Search,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Select, Loading, Button, Modal, Input } from '../../components/ui';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../../services/api';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useAuthStore } from '../../store/authStore';
import { TingkatRisiko, ResolvedLocalItem } from '../../types';
import { formatDate } from '../../utils/formatDate';

const RISK_COLORS: Record<string, string> = {
  rendah: '#22C55E',
  sedang: '#F59E0B',
  tinggi: '#F97316',
  kritis: '#EF4444',
  critical: '#EF4444',
};

interface KpiData {
  total_laporan: number;
  menunggu_validasi: number;
  dalam_proses: number;
  selesai: number;
  selesai_lokal: number;
  overdue: number;
  menunggu_konfirmasi: number;
  rata_rata_penyelesaian: number;
}

interface PendingItem {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  risiko?: string;
  unit?: { id: number; name: string };
  user?: { id: number; name: string };
  created_at?: string;
}

interface TindakLanjutItem {
  id: number;
  laporan_masalah_id?: number;
  laporan_masalah?: { id: number; kode: string; judul: string; status?: string };
  laporanMasalah?: { id: number; kode: string; judul: string; status?: string };
  target_date?: string;
  status: string;
  progress_percentage: number;
  pic_user?: { id: number; name: string };
  kondisi_selesai?: string;
}

interface AuditReport {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  resolution_level?: string | null;
  unit?: { name?: string | null } | null;
  user?: { name?: string | null } | null;
  target_efektif?: string | null;
  target_selesai?: string | null;
  created_at?: string | null;
}

interface AuditMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const DashboardDekan: React.FC = () => {
  const caps = useCapabilities();
  const { user } = useAuthStore();
  const fakultas = user?.unit?.name || 'Fakultas';
  const roleDisplay = user?.role?.display_name || 'Pimpinan Unit';

  // States
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<KpiData>({
    total_laporan: 0,
    menunggu_validasi: 0,
    dalam_proses: 0,
    selesai: 0,
    selesai_lokal: 0,
    overdue: 0,
    menunggu_konfirmasi: 0,
    rata_rata_penyelesaian: 0,
  });
  const [pendingValidasi, setPendingValidasi] = useState<PendingItem[]>([]);
  const [tindakLanjut, setTindakLanjut] = useState<TindakLanjutItem[]>([]);
  const [resolvedLocalList, setResolvedLocalList] = useState<ResolvedLocalItem[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [risikoData, setRisikoData] = useState<any[]>([]);
  const [periodeList, setPeriodeList] = useState<any[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditModalTitle, setAuditModalTitle] = useState('Semua Laporan');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReports, setAuditReports] = useState<AuditReport[]>([]);
  const [auditMeta, setAuditMeta] = useState<AuditMeta | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearchDraft, setAuditSearchDraft] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState('');

  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p: any) => p?.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };

    fetchPeriode();
  }, []);

  // Fetch data
  useEffect(() => {
    if (!isPeriodeReady) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const params: Record<string, any> = {};
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const [dashboardRes, validasiRes, tindakLanjutRes] = await Promise.all([
          api.get('/dashboard', { params }),
          // ✅ FIX: Tambahkan filter status eksplisit untuk hanya mengambil yang BELUM divalidasi
          api.get('/validasi/level1', {
            params: {
              per_page: 5,
              status: 'diajukan',  // ✅ Hanya ambil yang statusnya 'diajukan'
              ...params
            }
          }),
          api.get('/monitoring/ppepp-progress', { params: { per_page: 10, ...params } }),
        ]);

        // Parse dashboard
        const d = dashboardRes.data?.data || dashboardRes.data;
        if (d) {
          setKpiData({
            total_laporan: d.total_laporan || 0,
            menunggu_validasi: d.laporan_per_status?.diajukan || 0,
            dalam_proses:
              (d.laporan_per_status?.validasi_l1 || 0) +
              (d.laporan_per_status?.validasi_l2 || 0) +
              (d.laporan_per_status?.antrean_rapim || 0) +
              (d.laporan_per_status?.diputuskan || 0) +
              (d.laporan_per_status?.berlangsung || 0),
            selesai:
              (d.laporan_per_status?.selesai || 0) +
              (d.laporan_per_status?.diarsipkan || 0),
            selesai_lokal: d.resolved_local_count || 0,
            overdue: d.laporan_overdue || 0,
            menunggu_konfirmasi: d.laporan_per_status?.menunggu_konfirmasi || 0,
            rata_rata_penyelesaian: d.rata_rata_penyelesaian || 0,
          });
          setTrendData(d.tren_bulanan || []);
          setResolvedLocalList(d.resolved_local_list || []);

          // Risiko data
          const risiko = d.laporan_per_risiko || {};
          setRisikoData([
            { risiko: 'Rendah', jumlah: risiko.rendah || 0, fill: RISK_COLORS.rendah },
            { risiko: 'Sedang', jumlah: risiko.sedang || 0, fill: RISK_COLORS.sedang },
            { risiko: 'Tinggi', jumlah: risiko.tinggi || 0, fill: RISK_COLORS.tinggi },
            { risiko: 'Kritis', jumlah: risiko.kritis || 0, fill: RISK_COLORS.kritis },
          ]);
        }

        // ✅ FIX: Parse validasi L1 - filter hanya status 'diajukan'
        const validasiData = validasiRes.data?.data || validasiRes.data;
        let validasiList: PendingItem[] = [];

        if (Array.isArray(validasiData)) {
          validasiList = validasiData;
        } else if (validasiData?.data) {
          validasiList = validasiData.data;
        }

        // ✅ FIX: Filter tambahan di frontend untuk memastikan hanya 'diajukan'
        const filteredValidasi = validasiList.filter(
          (lap: PendingItem) => lap.status === 'diajukan'
        );

        setPendingValidasi(filteredValidasi.slice(0, 5));

        // Parse tindak lanjut
        const tlData = tindakLanjutRes.data?.data || tindakLanjutRes.data;
        if (Array.isArray(tlData)) {
          const filteredTl = tlData.filter((item: any) =>
            !['dibatalkan', 'batal'].includes((item.status || '').toLowerCase())
          );
          setTindakLanjut(filteredTl.slice(0, 5));
        } else if (tlData?.data) {
          const filteredTl = tlData.data.filter((item: any) =>
            !['dibatalkan', 'batal'].includes((item.status || '').toLowerCase())
          );
          setTindakLanjut(filteredTl.slice(0, 5));
        }

      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedPeriode, isPeriodeReady]);

  useEffect(() => {
    if (!showAuditModal) return;

    let cancelled = false;

    const fetchAuditReports = async () => {
      try {
        setAuditLoading(true);

        const params: Record<string, any> = {
          page: auditPage,
          per_page: 8,
        };

        if (selectedPeriode) params.periode_id = selectedPeriode;
        if (auditStatusFilter) params.status = auditStatusFilter;
        if (auditSearch.trim()) params.search = auditSearch.trim();

        const res = await api.get('/laporan', { params });
        if (cancelled) return;

        const payload = res.data || {};
        const items = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.data?.data)
            ? payload.data.data
            : [];
        const meta = payload.meta || payload.data?.meta || null;

        setAuditReports(items);
        setAuditMeta(meta);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load dean audit reports:', err);
          setAuditReports([]);
          setAuditMeta(null);
        }
      } finally {
        if (!cancelled) {
          setAuditLoading(false);
        }
      }
    };

    fetchAuditReports();

    return () => {
      cancelled = true;
    };
  }, [showAuditModal, auditPage, auditSearch, auditStatusFilter, selectedPeriode]);

  // ========== HELPER FUNCTIONS ==========

  /**
   * ✅ FIX: Cek apakah tindak lanjut sudah selesai atau menunggu konfirmasi
   */
  const isCompletedOrPending = (item: TindakLanjutItem): boolean => {
    const status = (item.status || '').toLowerCase();
    const kondisi = (item.kondisi_selesai || '').toLowerCase();
    const laporanStatus = (
      item.laporan_masalah?.status ||
      item.laporanMasalah?.status ||
      ''
    ).toLowerCase();
    const isLaporanFinal = ['selesai', 'diarsipkan'].includes(laporanStatus);

    // Cek berbagai kondisi selesai
    if (status === 'selesai') return true;
    if (status === 'validasi') return true; // Menunggu konfirmasi
    if (kondisi === 'selesai_final') return true;
    if (kondisi === 'menunggu_konfirmasi') return true;
    if (item.progress_percentage >= 100) return true;
    if (isLaporanFinal) return true;

    return false;
  };

  /**
   * ✅ FIX: Get status label untuk tindak lanjut
   */
  const getTindakLanjutStatusLabel = (item: TindakLanjutItem): { label: string; className: string } => {
    const status = (item.status || '').toLowerCase();
    const kondisi = (item.kondisi_selesai || '').toLowerCase();
    const progress = item.progress_percentage || 0;
    const laporanStatus = (
      item.laporan_masalah?.status ||
      item.laporanMasalah?.status ||
      ''
    ).toLowerCase();
    const isLaporanFinal = ['selesai', 'diarsipkan'].includes(laporanStatus);

    // Selesai final
    if (kondisi === 'selesai_final' || (status === 'selesai' && progress >= 100 && isLaporanFinal)) {
      return { label: '✓ Selesai', className: 'bg-success/10 text-success' };
    }

    // Menunggu konfirmasi
    if (
      kondisi === 'menunggu_konfirmasi'
      || status === 'validasi'
      || (progress >= 100 && status !== 'selesai' && !isLaporanFinal)
    ) {
      return { label: 'Menunggu Konfirmasi', className: 'bg-amber-100 text-amber-700' };
    }

    // Terhambat
    if (status === 'terhambat') {
      return { label: 'Terhambat', className: 'bg-danger/10 text-danger' };
    }

    // Dalam proses
    return { label: `${progress}%`, className: 'bg-warning/10 text-warning' };
  };

  /**
   * Helper: Days remaining - ✅ FIX: Return null jika sudah selesai
   */
  const getDaysRemaining = (item: TindakLanjutItem): number | null => {
    // ✅ FIX: Jika sudah selesai atau menunggu konfirmasi, tidak perlu tampilkan sisa hari
    if (isCompletedOrPending(item)) {
      return null;
    }

    if (!item.target_date) return null;

    return Math.ceil(
      (new Date(item.target_date).getTime() - new Date().setHours(0, 0, 0, 0)) /
      (1000 * 60 * 60 * 24)
    );
  };

  // Loading
  if (loading) {
    return <Loading variant="page" message="Memuat data..." />;
  }

  // Periode options
  const periodeOptions = [
    { value: '', label: 'Semua Periode' },
    ...periodeList.map((p) => ({
      value: String(p.id),
      label: `${p.nama}${p.is_active ? ' (Aktif)' : ''}`,
    })),
  ];
  const selectedPeriodeLabel = selectedPeriode
    ? periodeList.find((periode) => String(periode.id) === selectedPeriode)?.nama || 'Periode dipilih'
    : 'Semua Periode';
  const auditStatusOptions = [
    { value: '', label: 'Semua Status' },
    { value: 'diajukan', label: 'Menunggu Persetujuan' },
    { value: 'validasi_l1', label: 'Persetujuan Dekan' },
    { value: 'validasi_l2', label: 'Persetujuan Warek' },
    { value: 'antrean_rapim', label: 'Antrean RAPIM' },
    { value: 'diputuskan', label: 'Sudah Diputuskan' },
    { value: 'berlangsung', label: 'Sedang Dikerjakan' },
    { value: 'menunggu_konfirmasi', label: 'Menunggu Konfirmasi' },
    { value: 'diselesaikan_lokal', label: 'Ditangani Langsung' },
    { value: 'selesai', label: 'Selesai' },
    { value: 'diarsipkan', label: 'Diarsipkan' },
  ];
  const openAuditModal = (status = '', title = 'Semua Laporan Unit') => {
    setAuditModalTitle(title);
    setAuditPage(1);
    setAuditSearchDraft('');
    setAuditSearch('');
    setAuditStatusFilter(status);
    setShowAuditModal(true);
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: roleDisplay }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard {roleDisplay}
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <Building2 className="w-4 h-4 inline mr-1" />
            {fakultas}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openAuditModal('', 'Semua Laporan Unit')}
          >
            Semua Laporan
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => setShowGuide((prev) => !prev)}
          >
            Panduan
          </Button>
          <Select
            options={periodeOptions}
            value={selectedPeriode}
            onChange={setSelectedPeriode}
            className="w-full sm:w-56"
          />
        </div>
      </div>

      {showGuide && (
        <Card className="border border-teal-main/10 bg-gradient-to-r from-white via-white to-teal-main/5 animate-in">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-main/10 shrink-0">
                <Info className="w-5 h-5 text-teal-main" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-dark">Cara membaca dashboard</h2>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Panduan singkat agar Dekan atau Kepala Lembaga dapat membaca prioritas kerja, posisi laporan, dan status tindak lanjut dengan lebih jelas.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="text-neutral-text-secondary hover:text-teal-main transition-colors shrink-0"
              aria-label="Tutup panduan dashboard Dekan"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Butuh Persetujuan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menunjukkan laporan baru dari unit yang masih menunggu penelaahan dan keputusan awal Anda sebelum dapat dilanjutkan ke tahap berikutnya.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Sedang Berjalan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Mencakup laporan yang telah melewati persetujuan awal dan masih berada dalam proses validasi lanjutan, pembahasan, keputusan, atau pelaksanaan tindak lanjut oleh PIC.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Menunggu Konfirmasi</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menandakan tindak lanjut telah mencapai progres 100% dan sedang menunggu pengesahan akhir dari pihak yang memiliki kewenangan konfirmasi penyelesaian, yang saat ini diatur untuk Admin, Rektor, atau Wakil Rektor.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Tuntas via RAPIM</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Mencakup laporan yang telah dibahas dalam Rapat Pimpinan, diputuskan tindak lanjutnya, dikerjakan oleh PIC, lalu dinyatakan selesai atau diarsipkan setelah seluruh prosesnya tuntas.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Ditangani Langsung di Level Unit</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan laporan yang dapat diselesaikan langsung pada level Dekan atau Kepala Lembaga tanpa perlu dibawa ke RAPIM, sepanjang penyelesaiannya masih berada dalam kewenangan unit.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Alerts */}
      {kpiData.overdue > 0 && (
        <div className="p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-danger shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-danger">
              {kpiData.overdue} laporan sudah melewati deadline!
            </p>
          </div>
          <Link
            to="/laporan?status=overdue"
            className="text-xs font-semibold text-danger hover:underline shrink-0"
          >
            Lihat →
          </Link>
        </div>
      )}

      {/* ✅ FIX: Cek capability validate */}
      {kpiData.menunggu_validasi > 0 && caps.canValidate && (
        <div className="p-3 bg-warning/5 border border-warning/20 rounded-xl flex items-center gap-3">
          <CheckSquare className="w-5 h-5 text-warning shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-warning">
              {kpiData.menunggu_validasi} laporan menunggu persetujuan Anda
            </p>
          </div>
          <Link
            to="/validasi/level1"
            className="text-xs font-semibold text-warning hover:underline shrink-0"
          >
            Persetujuan Sekarang →
          </Link>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          {
            title: 'Total Laporan',
            value: kpiData.total_laporan,
            subtitle: 'Laporan pada unit Anda',
            icon: <FileText className="w-5 h-5" />,
            color: 'text-teal-main',
            bg: 'bg-teal-main/10',
            onClick: () => openAuditModal('', 'Semua Laporan Unit'),
          },
          ...(caps.canValidate ? [{
            title: 'Butuh Persetujuan',
            value: kpiData.menunggu_validasi,
            subtitle: 'Perlu keputusan awal',
            icon: <CheckSquare className="w-5 h-5" />,
            color: 'text-warning',
            bg: 'bg-warning/10',
            link: '/validasi/level1',
          }] : []),
          {
            title: 'Sedang Berjalan',
            value: kpiData.dalam_proses,
            subtitle: 'Masih dalam proses',
            icon: <Clock className="w-5 h-5" />,
            color: 'text-info',
            bg: 'bg-info/10',
          },
          ...(caps.has('confirm_completion') ? [{
            title: 'Menunggu Konfirmasi',
            value: kpiData.menunggu_konfirmasi,
            subtitle: 'Menunggu pengesahan Anda',
            icon: <Hourglass className="w-5 h-5" />,
            color: 'text-purple-600',
            bg: 'bg-purple-100',
            link: '/laporan?status=validasi',
          }] : [{
            title: 'Menunggu Konfirmasi',
            value: kpiData.menunggu_konfirmasi,
            subtitle: 'Menunggu pengesahan pimpinan',
            icon: <Hourglass className="w-5 h-5" />,
            color: 'text-gray-500',
            bg: 'bg-gray-100',
          }]),
          {
            title: 'Tuntas via RAPIM',
            value: kpiData.selesai,
            subtitle: 'Selesai setelah rapat',
            icon: <TrendingUp className="w-5 h-5" />,
            color: 'text-success',
            bg: 'bg-success/10',
          },
          {
            title: 'Ditangani Langsung',
            value: kpiData.selesai_lokal,
            subtitle: 'Selesai tanpa RAPIM',
            icon: <CheckSquare className="w-5 h-5" />,
            color: 'text-teal-main',
            bg: 'bg-teal-main/10',
            onClick: () => openAuditModal('diselesaikan_lokal', 'Laporan Diselesaikan di Level Unit'),
          },
          {
            title: 'Terlambat',
            value: kpiData.overdue,
            subtitle: 'Lewat target waktu',
            icon: <AlertTriangle className="w-5 h-5" />,
            color: 'text-danger',
            bg: 'bg-danger/10',
          },
        ].map((kpi: any, i) =>
          kpi.link ? (
            <Link key={i} to={kpi.link}>
              <Card hover className="h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                      {kpi.title}
                    </p>
                    <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                      {kpi.value}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-2 leading-relaxed">
                      {kpi.subtitle}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                    {kpi.icon}
                  </div>
                </div>
              </Card>
            </Link>
          ) : kpi.onClick ? (
            <button key={i} type="button" className="w-full text-left" onClick={kpi.onClick}>
              <Card hover className="h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                      {kpi.title}
                    </p>
                    <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                      {kpi.value}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-2 leading-relaxed">
                      {kpi.subtitle}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                    {kpi.icon}
                  </div>
                </div>
              </Card>
            </button>
          ) : (
            <Card key={i}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    {kpi.title}
                  </p>
                  <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                    {kpi.value}
                  </p>
                  <p className="text-xs text-neutral-text-secondary mt-2 leading-relaxed">
                    {kpi.subtitle}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>
                  {kpi.icon}
                </div>
              </div>
            </Card>
          )
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Validasi L1 */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-warning" />
              Menunggu Persetujuan Anda
            </h3>
            <Link
              to="/validasi/level1"
              className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1"
            >
              Lihat Semua <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {pendingValidasi.length > 0 ? (
              pendingValidasi.map((lap, i) => (
                <div
                  key={lap.id || i}
                  className="flex items-center gap-3 p-3 rounded-xl border border-neutral-border hover:bg-neutral-bg/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-teal-main">
                        {lap.kode}
                      </span>
                      <Badge
                        variant="risk"
                        risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko}
                      />
                    </div>
                    <p className="text-sm font-medium truncate">{lap.judul}</p>
                    <p className="text-xs text-neutral-text-secondary">
                      {lap.unit?.name || '-'} •{' '}
                      {lap.user?.name || '-'}
                    </p>
                  </div>
                  {/* ✅ FIX: Gunakan route yang benar untuk validasi */}
                  {/* ✅ FIX: Button review hanya jika bisa validate */}
                  {caps.canValidate && (
                    <Link to={`/laporan/${lap.id}`} state={{ fromValidation: true }}>
                      <button className="px-3 py-1.5 text-xs font-semibold text-white bg-warning rounded-lg hover:bg-warning/90 transition-colors">
                        Review
                      </button>
                    </Link>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckSquare className="w-10 h-10 mx-auto mb-2 text-success opacity-50" />
                <p className="text-sm text-neutral-text-secondary">
                  Tidak ada laporan menunggu persetujuan
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Risiko Distribution */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4">Distribusi Risiko</h3>
          <div className="space-y-3">
            {risikoData.map((item, i) => {
              const percentage =
                kpiData.total_laporan > 0
                  ? Math.round((item.jumlah / kpiData.total_laporan) * 100)
                  : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span className="text-sm font-medium">{item.risiko}</span>
                    </div>
                    <span className="text-sm font-bold">
                      {item.jumlah}{' '}
                      <span className="text-xs text-neutral-text-secondary font-normal">
                        ({percentage}%)
                      </span>
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.fill,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Tindak Lanjut di Fakultas - ✅ FIXED */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Tindak Lanjut di Unit
          </h3>
          <Link
            to="/laporan?scope=pic"
            className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1"
          >
            Lihat Semua <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="space-y-3">
          {tindakLanjut.length > 0 ? (
            tindakLanjut.map((item, i) => {
              // ✅ FIX: Gunakan helper function yang sudah diperbaiki
              const days = getDaysRemaining(item);
              const isOverdue = days !== null && days < 0;
              const isUrgent = days !== null && days >= 0 && days <= 7;
              const statusInfo = getTindakLanjutStatusLabel(item);
              const isCompleted = isCompletedOrPending(item);

              const judul =
                item.laporan_masalah?.judul ||
                item.laporanMasalah?.judul ||
                '-';
              const kode =
                item.laporan_masalah?.kode ||
                item.laporanMasalah?.kode ||
                '-';

              return (
                <div
                  key={item.id || i}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${isCompleted
                    ? 'border-success/20 bg-success/5'
                    : isOverdue
                      ? 'border-danger/30 bg-danger/5'
                      : isUrgent
                        ? 'border-warning/30 bg-warning/5'
                        : 'border-neutral-border hover:bg-neutral-bg/50'
                    }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs font-mono text-teal-main">
                        {kode}
                      </span>
                      {/* ✅ FIX: Status badge yang sudah diperbaiki */}
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                      {/* ✅ FIX: Hanya tampilkan sisa hari jika BELUM selesai */}
                      {days !== null && (
                        <span
                          className={`text-[10px] font-semibold ${isOverdue
                            ? 'text-danger'
                            : isUrgent
                              ? 'text-warning'
                              : 'text-neutral-text-secondary'
                            }`}
                        >
                          {isOverdue
                            ? `${Math.abs(days)} hari telat`
                            : `${days} hari lagi`}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium truncate">{judul}</p>
                    <p className="text-xs text-neutral-text-secondary">
                      PIC: {item.pic_user?.name || '-'}
                    </p>
                  </div>
                  <Link to={`/tindak-lanjut/${item.id}`}>
                    <button className="p-2 rounded-lg hover:bg-teal-main/10 text-neutral-text-secondary hover:text-teal-main transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
              );
            })
          ) : (
            <div className="text-center py-6">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
              <p className="text-sm text-neutral-text-secondary">
                Belum ada tindak lanjut aktif
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Laporan Diselesaikan Lokal */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-teal-main" />
            Diselesaikan di Level Unit
          </h3>
          <button
            type="button"
            onClick={() => openAuditModal('diselesaikan_lokal', 'Laporan Diselesaikan di Level Unit')}
            className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1"
          >
            Lihat Semua <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-3">
          {resolvedLocalList.length > 0 ? (
            resolvedLocalList.map((item, i) => (
              <div
                key={item.id || i}
                className="flex items-center gap-3 p-3 rounded-xl border border-success/20 bg-success/5 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-teal-main">
                      {item.kode}
                    </span>
                    <Badge
                      variant="risk"
                      risk={(item.tingkat_risiko || 'sedang') as TingkatRisiko}
                    />
                  </div>
                  <p className="text-sm font-medium truncate">{item.judul}</p>
                  <p className="text-xs text-neutral-text-secondary w-full truncate space-x-1">
                    <span>Oleh: {item.resolved_by || '-'}</span>
                    <span>•</span>
                    <span>{item.kategori || '-'}</span>
                  </p>
                </div>
                <Link to={`/laporan/${item.id}`}>
                  <button className="p-2 rounded-lg hover:bg-teal-main/10 text-neutral-text-secondary hover:text-teal-main transition-colors">
                    <Eye className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <CheckSquare className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
              <p className="text-sm text-neutral-text-secondary">
                Belum ada laporan yang diselesaikan lokal
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Trend */}
      <Card>
        <h3 className="text-base font-bold text-teal-dark mb-4">
          Tren Laporan 6 Bulan Terakhir
        </h3>
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6B7280' }} />
              <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '12px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="laporan_masuk"
                stroke="#0f33ab"
                strokeWidth={2.5}
                name="Laporan Masuk"
                dot={{ fill: '#0f33ab', r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="laporan_selesai"
                stroke="#22C55E"
                strokeWidth={2.5}
                name="Selesai"
                dot={{ fill: '#22C55E', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[250px] flex items-center justify-center text-neutral-text-secondary">
            Belum ada data tren
          </div>
        )}
      </Card>

      <Modal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        title={auditModalTitle}
        size="2xl"
        footer={(
          <Button variant="ghost" onClick={() => setShowAuditModal(false)}>
            Tutup
          </Button>
        )}
      >
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-dark">
                Daftar laporan periode {selectedPeriodeLabel}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Anda bisa melihat semua laporan pada periode ini. Gunakan pencarian atau pilih status untuk menemukan laporan yang ingin dilihat.
              </p>
            </div>
            <Badge className="self-start" size="sm">
              {auditMeta?.total ?? 0} laporan
            </Badge>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Total Unit
              </p>
              <p className="text-xl font-black text-teal-main mt-1">{kpiData.total_laporan}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Butuh Persetujuan
              </p>
              <p className="text-xl font-black text-warning mt-1">{kpiData.menunggu_validasi}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Tuntas via RAPIM
              </p>
              <p className="text-xl font-black text-success mt-1">{kpiData.selesai}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Ditangani Langsung
              </p>
              <p className="text-xl font-black text-teal-main mt-1">{kpiData.selesai_lokal}</p>
            </div>
          </div>

          <form
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px_auto_auto] gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setAuditPage(1);
              setAuditSearch(auditSearchDraft.trim());
            }}
          >
            <Input
              placeholder="Cari kode laporan, judul, atau unit"
              value={auditSearchDraft}
              onChange={(event) => setAuditSearchDraft(event.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
            <Select
              options={auditStatusOptions}
              value={auditStatusFilter}
              onChange={(value) => {
                setAuditPage(1);
                setAuditStatusFilter(value);
              }}
            />
            <Button type="submit" variant="outline">
              Cari
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAuditPage(1);
                setAuditSearchDraft('');
                setAuditSearch('');
                setAuditStatusFilter('');
              }}
            >
              Reset
            </Button>
          </form>

          <div className="rounded-2xl border border-neutral-border overflow-hidden">
            <div className="px-4 py-3 bg-neutral-bg border-b border-neutral-border flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-text-primary">Daftar Laporan</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Data di sini menyesuaikan periode yang sedang dipilih.
                </p>
              </div>
              {auditLoading && (
                <span className="text-xs text-neutral-text-secondary">Memuat data...</span>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-border">
              {auditLoading ? (
                <div className="py-12">
                  <Loading message="Memuat laporan..." />
                </div>
              ) : auditReports.length > 0 ? (
                auditReports.map((report) => (
                  <div key={report.id} className="p-4 bg-white hover:bg-neutral-bg/60 transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs font-mono font-semibold text-teal-main">{report.kode}</span>
                          <Badge variant="status" status={report.status} size="xs" useShortLabel />
                          {report.tingkat_risiko && (
                            <Badge variant="risk" risk={report.tingkat_risiko as TingkatRisiko} size="xs" />
                          )}
                        </div>
                        <p className="text-sm font-bold text-neutral-text-primary">{report.judul}</p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-text-secondary">
                          <span>Pelapor: {report.user?.name || '-'}</span>
                          <span>Unit: {report.unit?.name || '-'}</span>
                          <span>Target: {formatDate(report.target_efektif || report.target_selesai || '-')}</span>
                          <span>Dibuat: {formatDate(report.created_at || '-')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link to={`/laporan/${report.id}`}>
                          <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" />}>
                            Lihat Detail
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center">
                  <FileText className="w-10 h-10 mx-auto text-neutral-text-secondary mb-3" />
                  <p className="text-sm font-semibold text-neutral-text-primary">
                    Belum ada laporan yang cocok
                  </p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Coba ubah pencarian atau pilih status lain.
                  </p>
                </div>
              )}
            </div>
          </div>

          {auditMeta && auditMeta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-text-secondary">
                Halaman {auditMeta.current_page} dari {auditMeta.last_page}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page <= 1}
                  onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                >
                  Sebelumnya
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page >= auditMeta.last_page}
                  onClick={() => setAuditPage((prev) => (
                    auditMeta ? Math.min(auditMeta.last_page, prev + 1) : prev + 1
                  ))}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DashboardDekan;
